import { storage } from './index';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

const IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

// This mirrors the existing file-picker policy without introducing new file types.
const FILE_MIME_TYPES = new Set([
  ...IMAGE_MIME_TYPES,
  'audio/aac',
  'audio/flac',
  'audio/mpeg',
  'audio/mp4',
  'audio/ogg',
  'audio/wav',
  'audio/webm',
  'video/mp4',
  'video/ogg',
  'video/quicktime',
  'video/webm',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'text/plain',
]);

const IMAGE_UPLOAD_ERROR = 'Only JPEG, PNG, GIF, and WebP images up to 5 MiB can be uploaded.';
const FILE_TYPE_ERROR = 'This file type is not supported. Choose an image, audio, video, PDF, Office document, text, or CSV file up to 10 MiB.';
const FILE_SIZE_ERROR = 'Files must be 10 MiB or smaller.';

export const validateImageUpload = (file) => {
  if (!file || !IMAGE_MIME_TYPES.has(file.type) || file.size > 5 * 1024 * 1024) {
    return IMAGE_UPLOAD_ERROR;
  }
  return null;
};

export const validateFileUpload = (file) => {
  if (!file || !FILE_MIME_TYPES.has(file.type)) return FILE_TYPE_ERROR;
  if (file.size > 10 * 1024 * 1024) return FILE_SIZE_ERROR;
  return null;
};

export const getUploadErrorMessage = (error) => {
  if ([IMAGE_UPLOAD_ERROR, FILE_TYPE_ERROR, FILE_SIZE_ERROR].includes(error?.message)) {
    return error.message;
  }

  switch (error?.code) {
    case 'storage/unauthenticated':
      return 'Sign in with a verified email address before uploading files.';
    case 'storage/unauthorized':
      return 'Upload was denied. Verify your email address and try again.';
    case 'storage/canceled':
      return 'Upload canceled.';
    case 'storage/quota-exceeded':
      return 'Upload could not be completed because storage quota has been reached.';
    default:
      return 'Upload failed. Please try again.';
  }
};

const requireVerifiedOwner = async (userId) => {
  const auth = getAuth();

  if (!auth.currentUser) {
    throw new Error('Sign in with a verified email address before uploading files.');
  }

  await auth.currentUser.reload();
  if (!auth.currentUser.emailVerified) {
    throw new Error('Verify your email address before uploading files.');
  }
  await auth.currentUser.getIdToken(true);
  if (auth.currentUser.uid !== userId) {
    throw new Error('You can only upload files to your own account.');
  }

  return auth.currentUser;
};

const createSafeFilename = (file, fallbackName) => {
  const originalName = file.name || fallbackName;
  return originalName.replace(/[^a-zA-Z0-9._-]/g, '_');
};

export const uploadImage = async (file, userId) => {
  try {
    const validationError = validateImageUpload(file);
    if (validationError) throw new Error(validationError);

    await requireVerifiedOwner(userId);
    
    // Create a unique filename using timestamp
    // Ensure file has a name (pasted images might not have one)
    const fileName = createSafeFilename(file, `image_${new Date().getTime()}.png`);
    const timestamp = new Date().getTime();
    const filename = `${userId}/images/${timestamp}_${fileName}`;
    
    const storageRef = ref(storage, filename);

    // Upload the file with basic metadata
    // Ensure content type is set (default to png for images)
    const contentType = file.type || 'image/png';
    const metadata = {
      contentType: contentType
    };
    
    const snapshot = await uploadBytes(storageRef, file, metadata);
    
    // Return the URL exactly as provided by the Firebase SDK.
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

export const uploadFile = async (file, userId) => {
  try {
    const validationError = validateFileUpload(file);
    if (validationError) throw new Error(validationError);

    await requireVerifiedOwner(userId);

    // Create a unique filename using timestamp
    const timestamp = new Date().getTime();
    const filename = `${userId}/files/${timestamp}_${createSafeFilename(file, 'attachment')}`;
    
    const storageRef = ref(storage, filename);

    // Upload the file with basic metadata
    const metadata = {
      contentType: file.type
    };
    
    const snapshot = await uploadBytes(storageRef, file, metadata);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};
